import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  FileText,
  Eye,
  EyeOff,
  Search,
  Globe,
  Lock,
  ExternalLink,
  Download,
  LayoutGrid
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultPage = {
  title: '',
  slug: '',
  description: '',
  meta_title: '',
  meta_description: '',
  content: '',
  is_published: true
};

export default function AdminPages() {
  const { token } = useAuth();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState(defaultPage);
  const [activeTab, setActiveTab] = useState('content');

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/pages/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data);
    } catch (error) {
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingPage ? prev.slug : generateSlug(title),
      meta_title: prev.meta_title || title
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const pageData = {
        title: formData.title,
        slug: formData.slug,
        description: formData.description,
        meta_title: formData.meta_title || formData.title,
        meta_description: formData.meta_description || formData.description,
        sections: [{
          id: 'main-content',
          type: 'text',
          title: formData.title,
          content: formData.content,
          order: 0
        }],
        is_published: formData.is_published
      };

      if (editingPage) {
        await axios.put(`${API_URL}/api/pages/${editingPage.id}`, pageData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Page updated');
      } else {
        await axios.post(`${API_URL}/api/pages`, pageData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Page created');
      }
      
      setIsDialogOpen(false);
      setEditingPage(null);
      setFormData(defaultPage);
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save page');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    const mainSection = page.sections?.find(s => s.type === 'text') || {};
    setFormData({
      title: page.title,
      slug: page.slug,
      description: page.description || '',
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      content: mainSection.content || '',
      is_published: page.is_published
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Delete this page? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page deleted');
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete page');
    }
  };

  const handleTogglePublish = async (page) => {
    try {
      await axios.put(`${API_URL}/api/pages/${page.id}`, 
        { is_published: !page.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(page.is_published ? 'Page unpublished' : 'Page published');
      fetchPages();
    } catch (error) {
      toast.error('Failed to update page');
    }
  };

  const handleSeedPages = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/pages/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed pages');
    }
  };

  // Separate system pages and custom pages
  const systemPages = pages.filter(p => p.is_system);
  const customPages = pages.filter(p => !p.is_system);

  const filteredSystemPages = systemPages.filter(page => 
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCustomPages = customPages.filter(page => 
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="admin-pages-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Pages Management</h1>
          <p className="text-muted-foreground mt-1">{pages.length} pages total</p>
        </div>
        
        <div className="flex gap-2">
          {pages.length === 0 && (
            <Button variant="outline" onClick={handleSeedPages} data-testid="seed-pages-btn">
              <Download className="w-4 h-4 mr-2" />
              Load Default Pages
            </Button>
          )}
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingPage(null);
              setFormData(defaultPage);
              setActiveTab('content');
            }
          }}>
            <DialogTrigger asChild>
              <Button className="btn-emerald" data-testid="add-page-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Page
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPage ? 'Edit Page' : 'Create New Page'}</DialogTitle>
              </DialogHeader>
              
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="content">Content</TabsTrigger>
                  <TabsTrigger value="seo">SEO</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                <form onSubmit={handleSubmit}>
                  <TabsContent value="content" className="space-y-4">
                    <div>
                      <Label>Page Title *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        placeholder="e.g., About Us"
                        required
                        data-testid="page-title-input"
                      />
                    </div>

                    <div>
                      <Label>URL Slug *</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">/</span>
                        <Input
                          value={formData.slug}
                          onChange={(e) => setFormData({...formData, slug: e.target.value})}
                          placeholder="about-us"
                          required
                          disabled={editingPage?.is_system}
                          data-testid="page-slug-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        URL: thehotelemerald.in/{formData.slug}
                      </p>
                    </div>

                    <div>
                      <Label>Short Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        placeholder="Brief description of this page..."
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label>Page Content</Label>
                      <Textarea
                        value={formData.content}
                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                        placeholder="Write your page content here... (Supports Markdown)"
                        rows={12}
                        className="font-mono text-sm"
                        data-testid="page-content-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Supports Markdown formatting for rich text
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="seo" className="space-y-4">
                    <div>
                      <Label>Meta Title</Label>
                      <Input
                        value={formData.meta_title}
                        onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                        placeholder={formData.title || "Page title for search engines"}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {(formData.meta_title || formData.title).length}/60 characters
                      </p>
                    </div>

                    <div>
                      <Label>Meta Description</Label>
                      <Textarea
                        value={formData.meta_description}
                        onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                        placeholder="Description for search engines..."
                        rows={3}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {formData.meta_description.length}/160 characters recommended
                      </p>
                    </div>

                    {/* SEO Preview */}
                    <div className="bg-muted p-4 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-2">Search Preview</p>
                      <p className="text-blue-600 text-lg">{formData.meta_title || formData.title || 'Page Title'}</p>
                      <p className="text-emerald-700 text-sm">thehotelemerald.in/{formData.slug || 'page-url'}</p>
                      <p className="text-muted-foreground text-sm mt-1">
                        {formData.meta_description || formData.description || 'Page description will appear here...'}
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="settings" className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                      <div>
                        <Label>Published</Label>
                        <p className="text-sm text-muted-foreground">Make this page visible to visitors</p>
                      </div>
                      <Switch
                        checked={formData.is_published}
                        onCheckedChange={(checked) => setFormData({...formData, is_published: checked})}
                      />
                    </div>
                  </TabsContent>

                  <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="btn-emerald" data-testid="save-page-btn">
                      {editingPage ? 'Update Page' : 'Create Page'}
                    </Button>
                  </div>
                </form>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search pages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* System Pages (Core Website Pages) */}
      {filteredSystemPages.length > 0 && (
        <>
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lock className="w-5 h-5" />
                Core Website Pages ({filteredSystemPages.length})
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                These pages are part of the core website structure. You can edit their content but cannot delete them.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {filteredSystemPages.map((page) => (
                  <div 
                    key={page.id}
                    className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                    data-testid={`system-page-${page.slug}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${page.is_published ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                        <LayoutGrid className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{page.title}</p>
                          <Badge variant="outline" className="text-xs">System</Badge>
                          {!page.is_published && <Badge variant="secondary" className="text-xs">Draft</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">/{page.slug}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.open(`/${page.slug === 'home' ? '' : page.slug}`, '_blank')}
                        title="View Page"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEdit(page)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Custom Pages */}
      <h2 className="font-serif text-xl mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        Custom Pages ({filteredCustomPages.length})
      </h2>
      
      {filteredCustomPages.length > 0 ? (
        <div className="grid gap-4">
          {filteredCustomPages.map((page) => (
            <Card key={page.id} data-testid={`page-card-${page.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`p-3 rounded-lg ${page.is_published ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                  <FileText className="w-6 h-6" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{page.title}</h3>
                    {!page.is_published && (
                      <Badge variant="secondary">Draft</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">/{page.slug}</p>
                  {page.description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{page.description}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => window.open(`/${page.slug}`, '_blank')}
                    title="View Page"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleTogglePublish(page)}
                    title={page.is_published ? 'Unpublish' : 'Publish'}
                  >
                    {page.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleEdit(page)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDelete(page.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">No custom pages yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            Create custom pages like Careers, FAQ, Terms of Service, etc.
          </p>
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Page
          </Button>
        </div>
      )}

      {/* Empty state when no pages at all */}
      {pages.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <LayoutGrid className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">No pages configured yet</p>
          <Button variant="outline" onClick={handleSeedPages}>
            <Download className="w-4 h-4 mr-2" />
            Load Default Website Pages
          </Button>
        </div>
      )}
    </div>
  );
}
